import { useState } from 'react';
import { useAppStore } from '../store/useAppStore.js';
import { submitCallRating } from '../api/client.js';
import { StarIcon, AlertIcon } from '../icons.js';
import { SafetyNotice } from './SafetyNotice.js';

export function RatingView() {
  const peer = useAppStore((s) => s.lastCallPeer);
  const sessionId = useAppStore((s) => s.lastSessionId);
  const setView = useAppStore((s) => s.setView);
  const setLastSessionId = useAppStore((s) => s.setLastSessionId);
  const setLastCallPeer = useAppStore((s) => s.setLastCallPeer);
  const refreshProfile = useAppStore((s) => s.refreshProfile);

  const [stars, setStars] = useState<number>(0);
  const [hover, setHover] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSafetyDraft, setHasSafetyDraft] = useState(false);

  /** True to proceed. Warns when an unsent safety report would be discarded —
   *  the exits sit above the report form, so leaving silently would throw away
   *  what the user wrote. Used by leave(), and separately by onSubmit() so the
   *  rating path checks once and then clears the store directly. */
  function confirmDiscardingDraft(): boolean {
    if (!hasSafetyDraft) return true;
    return window.confirm(
      'You have an unsent safety report. Leave this page and discard it?',
    );
  }

  function leave() {
    if (!confirmDiscardingDraft()) return;
    setLastSessionId(null);
    setLastCallPeer(null);
    setView('lobby');
  }

  async function onSubmit() {
    if (!sessionId || stars < 1) return;
    if (!confirmDiscardingDraft()) return;
    setError(null);
    setSubmitting(true);
    try {
      await submitCallRating(sessionId, stars);
      // Reload the lobby roster so the guide's card immediately reflects the
      // new rating count + average. Best-effort: if the refresh fails the user
      // still routes to the lobby — they'd see the stale number until the next
      // navigation, but they're not trapped here.
      await refreshProfile().catch(() => { /* ignore — stale roster is fine */ });
      setLastSessionId(null);
      setLastCallPeer(null);
      setView('lobby');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const highlight = hover || stars;
  const canSubmit = sessionId != null && stars >= 1 && !submitting;

  return (
    <div className="max-w-md mx-auto card p-7 lg:p-10 has-grain animate-fade-up text-center">
      <h2 className="h-card mb-2 text-ink-900 dark:text-ink-100">
        Rate your session with {peer ?? 'your peer'}
      </h2>
      <p className="hint mb-6">Pick 1–5 stars. You can skip if you'd rather not.</p>

      <div className="flex justify-center gap-2 mb-6">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n === 1 ? '' : 's'}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(n)}
            onBlur={() => setHover(0)}
            onClick={() => setStars(n)}
            className="p-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <StarIcon
              filled={n <= highlight}
              width={36}
              height={36}
              className={n <= highlight
                ? 'text-brand dark:text-brand-bright'
                : 'text-ink-300 dark:text-ink-600'}
            />
          </button>
        ))}
      </div>

      {sessionId == null ? (
        <p className="hint mb-5">Couldn't link to this call — you can return to the lobby.</p>
      ) : null}

      <div className="flex items-center justify-center gap-5">
        {sessionId != null ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => { void onSubmit(); }}
            disabled={!canSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        ) : null}
        <button
          type="button"
          className={sessionId != null ? 'btn-ghost' : 'btn-primary'}
          onClick={leave}
        >
          Return to Lobby
        </button>
      </div>

      {sessionId != null ? (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={leave}
            className="text-[0.9rem] text-ink-600 dark:text-ink-300 hover:underline"
          >
            Skip rating
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="notice-error mt-5 text-left">
          <AlertIcon className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}

      <SafetyNotice sessionId={sessionId} peer={peer} onDraftChange={setHasSafetyDraft} />
    </div>
  );
}
